import React, { useCallback, useEffect, useState } from "react";
import { ScrollText, Loader2, Crown } from "lucide-react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { avatarSrc } from "@/lib/defaults";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

// Lot 608 — « Journal de la page » : qui a publié ou modifié quoi (responsable, gestionnaire, admin) et quand.
// Visible du responsable de l'église (et des admins) uniquement.
const FALLBACK = {
  updated: "a modifié la présentation ({fields})", post: "a publié une publication au nom de l'église", post_scheduled: "a programmé une publication",
  story: "a publié une annonce", story_scheduled: "a programmé une annonce", story_scheduled_cancelled: "a annulé une annonce programmée",
  story_scheduled_updated: "a modifié une annonce programmée", template_created: "a créé le modèle « {name} »", template_renamed: "a renommé un modèle en « {name} »",
  service_cancelled: "a annulé le culte « {title} » du {date} à {time}", service_restored: "a rétabli le culte « {title} » du {date} à {time}",
  template_deleted: "a supprimé le modèle « {name} »", event: "a créé l'événement « {title} »", live_attached: "a rattaché un direct à la page",
  live_detached: "a détaché un direct de la page", live_look: "a réglé le rendu des directs (filtre {fx}, lissage {beauty} %)", manager_invited: "a invité {name} à gérer la page", manager_invite_cancelled: "a annulé une invitation",
  manager_accepted: "a accepté de gérer la page", manager_declined: "a refusé de gérer la page", manager_removed: "a retiré {name} des gestionnaires",
  manager_left: "ne gère plus la page", manager_reinvited: "a ré-invité {name} à gérer la page",
};
const FIELD_FALLBACK = {
  description: "description", address: "adresse", website: "site web", handle: "identifiant @", pastor_name: "nom du responsable", pastor_title: "titre du responsable",
  logo_url: "logo", cover_url: "couverture", cover_video_url: "bannière vidéo", service_schedule: "horaires des cultes", share_accent: "couleur de partage",
  name: "nom", city: "ville", country: "pays", denomination: "dénomination", phone: "téléphone", email: "e-mail",
};
const SHORT = 8;
const fmt = (iso, lang) => { try { return new Date(iso).toLocaleString(lang || "fr", { dateStyle: "medium", timeStyle: "short" }); } catch { return iso || ""; } };

export const ChurchActivityCard = ({ church }) => {
  const { t, lang } = useApp();
  const [entries, setEntries] = useState(null);
  const [all, setAll] = useState(false);
  const load = useCallback(() => api.get(`/churches/${church.id}/activity`).then(({ data }) => setEntries(data.entries || [])).catch(() => setEntries([])), [church.id]);
  useEffect(() => { load(); }, [load]);

  const kindLabel = (k) => ({ owner: t("church_mgr.owner", "Responsable"), manager: t("church_mgr.manager", "Gestionnaire"), admin: t("church_log.kind_admin", "Équipe Yalola"), system: t("church_log.kind_system", "Automatique") }[k] || k);
  const line = (e) => {
    const m = e.meta || {};
    const fields = (m.fields || []).map((f) => t(`church_log.f_${f}`, FIELD_FALLBACK[f] || f)).join(", ");
    return t(`church_log.a_${e.action}`, FALLBACK[e.action] || e.action).replace("{fields}", fields).replace("{name}", m.name || "—").replace("{title}", m.title || "—")
      .replace("{fx}", m.fx ? t(`fx.preset_${m.fx}`, m.fx) : "—").replace("{beauty}", String(m.beauty ?? 0))
      .replace("{date}", m.date ? m.date.split("-").reverse().join("/") : "—").replace("{time}", m.time || "—"); // lot 723 — culte annulé / rétabli
  };
  const shown = entries ? (all ? entries : entries.slice(0, SHORT)) : [];

  return (
    <div className="yalola-card p-5" data-testid="church-activity-card">
      <div className="flex items-center gap-2 mb-1">
        <ScrollText size={17} style={{ color: "var(--yalola-primary)" }} />
        <h2 className="font-bold font-display">{t("church_log.title", "Journal de la page")}</h2>
        {entries && <span className="chip text-[10px] ml-auto" data-testid="church-activity-count">{entries.length}</span>}
      </div>
      <p className="text-xs mb-3" style={{ color: "var(--yalola-text-muted)" }}>{t("church_log.desc", "Qui a publié ou modifié quoi sur la page (responsable, gestionnaires, équipe Yalola) et quand. Visible par toi et les administrateurs.")}</p>
      {entries === null ? (
        <div className="py-5 flex justify-center"><Loader2 size={18} className="animate-spin" style={{ color: "var(--yalola-primary)" }} /></div>
      ) : entries.length === 0 ? (
        <div className="text-sm py-3" style={{ color: "var(--yalola-text-muted)" }} data-testid="church-activity-empty">{t("church_log.empty", "Aucune action enregistrée pour le moment.")}</div>
      ) : (
        <ul className="divide-y" style={{ borderColor: "var(--yalola-border)" }} data-testid="church-activity-list" data-count={entries.length}>
          {shown.map((e, i) => (
            <li key={e.id} className="py-2.5 flex items-start gap-3" data-testid={`church-activity-${i}`} data-action={e.action} data-kind={e.actor_kind}>
              <Avatar className="h-8 w-8 mt-0.5"><AvatarImage src={avatarSrc(e.actor || {})} /><AvatarFallback>{(e.actor?.name || "?")[0]}</AvatarFallback></Avatar>
              <div className="flex-1 min-w-0">
                <div className="text-sm break-words" data-testid={`church-activity-text-${i}`}>
                  <b>{e.actor?.name || t("church_log.former_member", "Ancien membre")}</b> {line(e)}
                </div>
                {e.meta?.preview && <div className="text-xs italic truncate" style={{ color: "var(--yalola-text-muted)" }}>« {e.meta.preview} »</div>}
                <div className="text-[11px] flex items-center gap-1.5 flex-wrap mt-0.5" style={{ color: "var(--yalola-text-muted)" }}>
                  <span className="chip text-[10px] px-1.5 py-0 inline-flex items-center gap-0.5" data-testid={`church-activity-kind-${i}`}>{e.actor_kind === "owner" && <Crown size={9} />}{kindLabel(e.actor_kind)}</span>
                  <span>{fmt(e.at, lang)}</span>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
      {entries && entries.length > SHORT && (
        <button type="button" onClick={() => setAll((v) => !v)} className="mt-2 text-sm font-medium" style={{ color: "var(--yalola-primary)" }} data-testid="church-activity-toggle">
          {all ? t("security.less", "Réduire") : t("church_log.more", "Voir tout le journal ({n})").replace("{n}", String(entries.length))}
        </button>
      )}
    </div>
  );
};

export default ChurchActivityCard;
